import { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text, ActivityIndicator, type StyleProp, type ViewStyle } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { FONTS, SPACING, RADIUS, COLORS } from '@/lib/brand'
import { useSemanticColors, INTERACTION } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useVehicleDetail } from '@/lib/hooks/use-vehicle-detail'
import { useVehicleUnavailableRanges } from '@/lib/hooks/use-booking'
import { useBookingStore } from '@/lib/stores/booking.store'
import { toast } from '@/lib/toast'
import { selection } from '@/lib/utils/haptics'
import { tripPriceForVehicle } from '@/lib/utils/pricing'
import { usePairFee } from '@/lib/hooks/use-pair-fee'
import { RepeatPairFeeCard } from '@/components/booking/RepeatPairFeeCard'
import { CoverageStayReasonCard } from '@/components/booking/CoverageStayReasonCard'
import { vehicleHasPlatformCoverage } from '@/lib/utils/qualitas-asr'
import {
  rentalDaysBetween,
  formatSvDateDisplay,
  todayInBookingTimeZoneISO,
  toBookingDateTimeISO,
  bookingDateKey,
  bookingTimeHHmm,
  isBookingDateTimeInThePast,
} from '@/lib/utils/dates'
import { clampTimeToWindow, formatTime12h } from '@/lib/utils/time-slots'
import { earliestPickupTimeOnDate, firstBookableStart } from '@/lib/utils/booking-first-slot'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { Pressable as RNPressable } from '@/components/ui/Pressable'
import { TimeSlotPicker } from '@/components/ui/TimeSlotPicker'
import { DateRangeCalendar } from '@/components/ui/DateRangeCalendar'
import {
  applyDateRangeSelection,
  bookingCalendarDayBlock,
  type BookingCalendarBlock,
} from '@/lib/utils/date-range-calendar'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { FormActionBar } from '@/components/ui/FormActionBar'
import {
  BookingLocationPicker,
  type BookingLocationPickerMode,
} from '@/components/booking/BookingLocationPicker'
import {
  areBookingLocationsWithinRadius,
  resolveBookingLocations,
  type BookingLocationValue,
} from '@/lib/utils/booking-location'
import { getVehicleDisplayPriceCents } from '@/lib/utils/pricing'
import { formatCurrency, formatPrice } from '@/lib/utils/format'
import {
  normalizeVehicleMinRentalHours,
  vehicleMinRentalDayCount,
} from '@/lib/utils/min-rental-duration'
import { calculateIndependenceCampaignDiscountCents } from '@/lib/utils/pricing-core'
import { AlertCircle, CheckCircle, Clock3, MapPin, Pencil } from '@/components/ui/icons'
import { useProductTelemetryScroll } from '@/lib/hooks/use-product-telemetry-scroll'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'

type ActiveDateField = 'pickup' | 'return'

function addDaysISO(iso: string, days: number): string {
  const date = new Date(`${iso}T12:00:00`)
  date.setDate(date.getDate() + days)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function datesInRangeISO(start: string, end: string): string[] {
  const dates: string[] = []
  const current = new Date(`${start}T12:00:00`)
  const last = new Date(`${end}T12:00:00`)
  while (current <= last) {
    const y = current.getFullYear()
    const m = String(current.getMonth() + 1).padStart(2, '0')
    const d = String(current.getDate()).padStart(2, '0')
    dates.push(`${y}-${m}-${d}`)
    current.setDate(current.getDate() + 1)
  }
  return dates
}

function firstAvailableDateFrom(
  start: string,
  unavailableDateSet: Set<string>,
  maxDaysAhead = 540,
): string | null {
  let candidate = start
  for (let offset = 0; offset <= maxDaysAhead; offset += 1) {
    if (!unavailableDateSet.has(candidate)) return candidate
    candidate = addDaysISO(candidate, 1)
  }
  return null
}

function bookingAmountBucket(cents: number | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return 'unknown'
  if (cents < 2500) return 'lt_25'
  if (cents < 5000) return '25_49'
  if (cents < 10000) return '50_99'
  if (cents < 20000) return '100_199'
  return 'gte_200'
}

export default function BookingScreen() {
  const { vehicleId, e2ePreset } = useLocalSearchParams<{
    vehicleId: string
    e2ePreset?: string | string[]
  }>()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const router = useRouter()
  // `useIsFocused` from `@react-navigation/native` threw "Couldn't find a
  // navigation object" here under React 19 — the only screen in the app that
  // used it — and crashed the whole booking flow the moment a verified guest
  // tapped "Reservar ahora" (web and mobile). Every navigation hook in that
  // library resolves through the NavigationContainer context, which is what
  // was missing at mount. The screen already has expo-router's global
  // `useRouter` (context-free); a plain `true` keeps `setVehicle` running on
  // mount so the durable booking draft is always set, and focus churn (a modal
  // on top, an app-switch) can never render the screen dead.
  const isFocused = true
  const { isDesktop, isTablet } = useBreakpoint()
  const timeFieldsInline = isDesktop || isTablet

  // Se consumen las tres cosas que el hook devuelve, no solo el dato.
  //
  // Antes se descartaban `isLoading`, `error` y `refetch`. Si la ficha del
  // vehiculo no cargaba, `canContinue` quedaba en falso por `!!vehicle` y
  // `!!breakdown` PARA SIEMPRE: sin tarjeta del carro, sin precio, sin un solo
  // mensaje y sin forma de reintentar. La pantalla se veia a medio dibujar y el
  // boton muerto. Es el mismo callejon que la disponibilidad ya tenia resuelto
  // veinte lineas mas abajo, con su aviso y su boton de reintentar.
  const {
    vehicle,
    isLoading: vehicleLoading,
    error: vehicleError,
    refetch: refetchVehicle,
  } = useVehicleDetail(vehicleId)
  const {
    guestServiceFeePct,
    completedPairTrips,
    isReady: pairFeeReady,
  } = usePairFee(vehicle?.host.id)
  const e2ePresetValue = Array.isArray(e2ePreset) ? e2ePreset[0] : e2ePreset
  const isE2eCheckoutPreset = e2ePresetValue === 'checkout' && vehicleId?.startsWith('e2e-')
  const [activeDateField, setActiveDateField] = useState<ActiveDateField>('pickup')
  const [locationPickerMode, setLocationPickerMode] = useState<BookingLocationPickerMode | null>(
    null,
  )

  const {
    startDate,
    endDate,
    startTime,
    endTime,
    insuranceTier,
    setVehicle,
    setDates,
    setStartTime,
    setEndTime,
    pickupAddress,
    pickupLat,
    pickupLng,
    dropoffAddress,
    dropoffLat,
    dropoffLng,
    isSameLocation,
    setPickup,
    setDropoff,
    setSameLocation,
    setPricing,
  } = useBookingStore()

  // Keep a single durable booking draft per vehicle. `setVehicle` resets only
  // when the vehicle actually changes or the persisted draft expired; focus
  // changes, modal closes, browser refreshes, and back/forward navigation must
  // never erase dates, hours, or delivery points mid-booking.
  useEffect(() => {
    if (!isFocused || !vehicleId) return
    setVehicle(vehicleId)
    if (isE2eCheckoutPreset) {
      setDates(null, null)
      setStartTime(null)
      setEndTime(null)
      setActiveDateField('pickup')
    }
  }, [isFocused, isE2eCheckoutPreset, setDates, setEndTime, setStartTime, setVehicle, vehicleId])

  const minDate = todayInBookingTimeZoneISO()
  const tomorrow = useMemo(() => addDaysISO(minDate, 1), [minDate])
  const availabilityToDate = useMemo(() => addDaysISO(minDate, 540), [minDate])
  const minRentalHours = useMemo(
    () => normalizeVehicleMinRentalHours(vehicle?.minRentalHours),
    [vehicle?.minRentalHours],
  )
  const minRentalDays = useMemo(() => vehicleMinRentalDayCount(minRentalHours), [minRentalHours])
  const minimumReturnDayOffset = minRentalDays ?? 1
  const {
    ranges: unavailableRanges,
    isLoading: unavailableRangesLoading,
    error: unavailableRangesError,
    refetch: refetchAvailability,
  } = useVehicleUnavailableRanges(vehicleId, minDate, availabilityToDate)
  const availabilityReady = !unavailableRangesLoading && !unavailableRangesError
  const unavailableDateSet = useMemo(() => {
    const dates = new Set<string>()
    for (const range of unavailableRanges) {
      // Un dia de CAMBIO no es un dia bloqueado: es un dia elegible con la hora
      // acotada. Meterlo aqui volveria a matar el dia de inventario que liberar
      // el cambio vino a recuperar -- y en el borde de inicio dejaria fuera un
      // dia en el que todavia se puede devolver.
      if (range.reason === 'changeover') continue
      for (const date of datesInRangeISO(range.startDate, range.endDate)) {
        dates.add(date)
      }
    }
    return dates
  }, [unavailableRanges])

  /**
   * La hora mas temprana a la que se puede recoger en cada dia FRONTERA.
   *
   * El calendario ya no bloquea el dia de la devolucion de la renta anterior:
   * eso recupera un dia de inventario por renta. Pero ese dia no esta libre
   * entero -- hay un colchon de cambio entre una devolucion y la siguiente
   * recogida-- y el servidor rechaza cualquier hora anterior.
   *
   * Sin este mapa, la pantalla abriria el dia y ofreceria las 48 horas para que
   * el servidor niegue las primeras: exactamente el callejon sin salida que
   * liberar el dia venia a quitar, movido una pantalla mas adelante.
   *
   * `free_from` lo calcula el servidor con la MISMA regla que usa para
   * rechazar, asi que aqui no se re-deriva nada: solo se lee.
   */
  const { earliestPickupByDate, latestReturnByDate } = useMemo(() => {
    // `bookingDateKey` / `bookingTimeHHmm` ya resuelven la zona horaria del
    // negocio; re-derivarla aqui seria la enesima fuente de verdad.
    const desde = new Map<string, string>()
    const hasta = new Map<string, string>()
    for (const range of unavailableRanges) {
      if (range.freeFrom) {
        const dia = bookingDateKey(range.freeFrom)
        const hora = bookingTimeHHmm(range.freeFrom)
        // El mas TARDE gana: si dos rentas se liberan el mismo dia, el carro no
        // esta disponible hasta que se cumpla el colchon de la ultima.
        const previo = dia ? desde.get(dia) : undefined
        if (dia && hora && (!previo || hora > previo)) desde.set(dia, hora)
      }
      if (range.freeUntil) {
        const dia = bookingDateKey(range.freeUntil)
        const hora = bookingTimeHHmm(range.freeUntil)
        // El mas TEMPRANO gana, por la razon simetrica.
        const previo = dia ? hasta.get(dia) : undefined
        if (dia && hora && (!previo || hora < previo)) hasta.set(dia, hora)
      }
    }
    return { earliestPickupByDate: desde, latestReturnByDate: hasta }
  }, [unavailableRanges])

  const firstBookable = useMemo(
    () =>
      firstBookableStart({
        unavailableDateSet,
        earliestPickupByDate,
      }),
    [earliestPickupByDate, unavailableDateSet],
  )

  const firstAvailableStartDate = firstBookable?.date ?? null

  const firstAvailableReturnDate = useMemo(() => {
    const baseDate = startDate || firstAvailableStartDate
    if (!baseDate) return null
    return firstAvailableDateFrom(addDaysISO(baseDate, minimumReturnDayOffset), unavailableDateSet)
  }, [firstAvailableStartDate, minimumReturnDayOffset, startDate, unavailableDateSet])

  const defaultReturnDate = useMemo(() => {
    if (!firstBookable) return null
    return firstAvailableDateFrom(
      addDaysISO(firstBookable.date, minimumReturnDayOffset),
      unavailableDateSet,
    )
  }, [firstBookable, minimumReturnDayOffset, unavailableDateSet])

  const minimumReturnDate = useMemo(() => {
    if (!startDate) return null
    return addDaysISO(startDate, minimumReturnDayOffset)
  }, [minimumReturnDayOffset, startDate])

  // Earliest day after the pickup date on which the vehicle is already booked.
  // A return date on or past it would make the trip span that day, so it must
  // not be selectable: before this, such a date could be tapped, showed up as
  // selected, and was then wiped by the range-validation effect below with
  // nothing on screen saying why.
  const firstBlockedDateAfterStart = useMemo(() => {
    if (!startDate || unavailableDateSet.size === 0) return null
    let earliest: string | null = null
    for (const date of unavailableDateSet) {
      if (date <= startDate) continue
      if (!earliest || date < earliest) earliest = date
    }
    return earliest
  }, [startDate, unavailableDateSet])

  const maxSelectableReturnDate = useMemo(
    () => (firstBlockedDateAfterStart ? addDaysISO(firstBlockedDateAfterStart, -1) : null),
    [firstBlockedDateAfterStart],
  )

  // Every candidate return date is either too early for the minimum duration or
  // on the far side of a booked day — the guest has to move the pickup date.
  const hasNoSelectableReturnDate = Boolean(
    startDate &&
    minimumReturnDate &&
    maxSelectableReturnDate &&
    maxSelectableReturnDate < minimumReturnDate,
  )

  const days = useMemo(() => {
    if (!startDate || !endDate) return 0
    return rentalDaysBetween(new Date(startDate), new Date(endDate))
  }, [startDate, endDate])

  /*
   * LA HORA TIENE QUE SEGUIR SIENDO VALIDA PARA EL DIA QUE QUEDO ELEGIDO.
   *
   * Cambiar la fecha no toca startTime/endTime: setDates reescribe los dias y
   * las horas sobreviven. En un dia de cambio eso deja una hora fuera de la
   * ventana del blocked_until. El TimeSlotPicker apaga las horas, pero el valor
   * elegido se seguia mostrando como valido, y canContinue --que solo
   * preguntaba !!endTime-- encendia Continuar.
   *
   * No se borra. UIDatePicker tampoco borra: ajusta al borde valido mas
   * cercano. El huesped ya eligio una hora; vaciar el campo y pedirle que
   * vuelva a tocar es un callejon en la ruta del dinero. El campo y el chip
   * saltan a la primera (o ultima) hora libre, y el texto bajo el picker
   * dice por que el resto del dia esta apagado.
   *
   * Si no queda ningun slot --min despues de 23:30, o max antes de 00:00--
   * ahi si se suelta, y el error vive en el picker, no en un toast.
   *
   * La derivacion manda sobre el store por un frame: el picker recibe la hora
   * ya ajustada, canContinue no se enciende con la invalida, y un effect
   * escribe el ajuste para que checkout y el borrador persistan lo mismo.
   *
   * Hoy esto esta DORMIDO --sin rentas vivas no hay rangos changeover, los
   * dos Map salen vacios y no hay ventana que violar-- y se enciende con la
   * primera reserva real del carro.
   */
  const pickupWindowStart = startDate
    ? (earliestPickupTimeOnDate({
        date: startDate,
        changeoverMin: earliestPickupByDate.get(startDate),
      }) ?? '23:59')
    : undefined
  const dropoffWindowEnd = endDate ? latestReturnByDate.get(endDate) : undefined
  const pickupTime = clampTimeToWindow(startTime, pickupWindowStart)
  const dropoffTime = clampTimeToWindow(endTime, undefined, dropoffWindowEnd)
  const pickupWindowImpossible = Boolean(startTime && pickupTime === null)
  const dropoffWindowImpossible = Boolean(endTime && dropoffTime === null)

  /**
   * `null` = TODAVIA NO SE PUEDE MEDIR. No es lo mismo que cero.
   *
   * Devolvia 0 cuando faltaba cualquiera de las cuatro piezas, y eso colapsaba
   * dos estados que significan cosas opuestas: "aun no se sabe" y "dura menos
   * del minimo". Como las horas nacen vacias en TODA reserva nueva, apenas se
   * elegian las dos fechas la duracion valia 0, `meetsVehicleMinimumDuration`
   * daba falso y la pantalla pintaba en ROJO "Ajusta la fecha u hora de
   * devolucion: este carro requiere minimo N dias" -- sobre una seleccion
   * correcta, y con CUALQUIER rango, tambien uno de diez dias.
   *
   * Lo peor: la accion que ese mensaje pedia no lo apagaba. Mover la fecha mas
   * lejos dejaba la duracion en 0 igual; lo unico que lo quitaba era elegir las
   * horas, que es justo lo que el mensaje no nombraba como faltante.
   *
   * Alcance medido en produccion el 2026-08-26: 24 de los 30 carros activos
   * declaran min_rental_hours (72h en 23, 48h en 1). El 80% del inventario
   * mostraba un error falso en la pantalla del dinero.
   */
  const selectedDurationHours = useMemo<number | null>(() => {
    if (!startDate || !endDate || !pickupTime || !dropoffTime) return null
    const start = new Date(toBookingDateTimeISO(startDate, pickupTime)).getTime()
    const end = new Date(toBookingDateTimeISO(endDate, dropoffTime)).getTime()
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
    return Math.round((end - start) / 3_600_000)
  }, [dropoffTime, endDate, pickupTime, startDate])

  /**
   * Indeterminado NO acusa. Solo se afirma que la duracion es insuficiente
   * cuando hay una duracion realmente medida por debajo del minimo.
   *
   * Esto no afloja el candado: `canContinue` sigue exigiendo las horas por su
   * cuenta, asi que el boton no se enciende antes de tiempo, y el servidor
   * valida la duracion igual (trigger de min_rental_duration + booking.service).
   * Lo unico que cambia es que la pantalla deja de acusar mientras no tiene
   * con que.
   */
  const meetsVehicleMinimumDuration =
    !minRentalHours || selectedDurationHours === null || selectedDurationHours >= minRentalHours

  // Calculate pricing whenever dates or vehicle change
  const breakdown = useMemo(() => {
    if (!vehicle || days < 1) return null
    // Del vehiculo, no de una lista de parametros que esta pantalla arme: asi
    // no puede volver a mostrar un total sin deposito mientras el checkout
    // muestra otro con el.
    const preDiscount = tripPriceForVehicle(
      {
        ...vehicle,
        // DESACOPLE DE LA PRIMA (P3): la línea de cobertura se decide por la
        // cobertura del vehículo; own_policy/exempt llevan prima 0 y el servidor
        // cobra lo mismo (insuranceCents = 0 en el cargo autoritativo).
        hasPlatformCoverage: vehicleHasPlatformCoverage(vehicle),
      },
      {
        days,
        insuranceTier,
        promoDiscountCents: 0,
        guestServiceFeePct,
      },
    )
    if (!startDate || !endDate) return preDiscount

    const campaignDiscountCents = calculateIndependenceCampaignDiscountCents({
      startAt: startDate && pickupTime ? toBookingDateTimeISO(startDate, pickupTime) : startDate,
      endAt: endDate && dropoffTime ? toBookingDateTimeISO(endDate, dropoffTime) : endDate,
      serviceFeeCents: preDiscount.platformFeeCents,
    })

    return campaignDiscountCents > 0
      ? tripPriceForVehicle(
          {
            ...vehicle,
            hasPlatformCoverage: vehicleHasPlatformCoverage(vehicle),
          },
          {
            days,
            insuranceTier,
            promoDiscountCents: campaignDiscountCents,
            guestServiceFeePct,
          },
        )
      : preDiscount
  }, [
    dropoffTime,
    endDate,
    guestServiceFeePct,
    insuranceTier,
    pickupTime,
    startDate,
    vehicle,
    days,
  ])
  const isIndependenceCampaignApplied = (breakdown?.promoDiscountCents ?? 0) > 0

  // Sync pricing to store
  useEffect(() => {
    if (isFocused && breakdown) setPricing(breakdown)
  }, [isFocused, breakdown, setPricing])

  const selectedRangeHasUnavailable = useMemo(() => {
    if (!startDate) return false
    if (!endDate) return unavailableDateSet.has(startDate)
    return datesInRangeISO(startDate, endDate).some((date) => unavailableDateSet.has(date))
  }, [endDate, startDate, unavailableDateSet])

  const resolvedLocations = useMemo(
    () =>
      resolveBookingLocations({
        vehicleHomeAddress: vehicle?.homeAddress,
        vehicleHomeLocation: vehicle?.homeLocation,
        pickupAddress,
        pickupLat,
        pickupLng,
        dropoffAddress,
        dropoffLat,
        dropoffLng,
      }),
    [
      dropoffAddress,
      dropoffLat,
      dropoffLng,
      pickupAddress,
      pickupLat,
      pickupLng,
      vehicle?.homeAddress,
      vehicle?.homeLocation,
    ],
  )
  const {
    hasVehicleHomeLocation,
    hasDraftPickupLocation,
    hasDraftDropoffLocation,
    pickupLocation,
    dropoffLocation,
  } = resolvedLocations

  useEffect(() => {
    if (!isFocused || !hasVehicleHomeLocation || !pickupLocation || !dropoffLocation) return
    if (!hasDraftPickupLocation) {
      setPickup(pickupLocation.address, pickupLocation.lat, pickupLocation.lng)
    }
    if (!hasDraftDropoffLocation) {
      setDropoff(dropoffLocation.address, dropoffLocation.lat, dropoffLocation.lng)
    }
    if (!hasDraftPickupLocation && !hasDraftDropoffLocation) setSameLocation(true)
  }, [
    isFocused,
    dropoffLocation,
    hasDraftDropoffLocation,
    hasDraftPickupLocation,
    hasVehicleHomeLocation,
    pickupLocation,
    setDropoff,
    setPickup,
    setSameLocation,
  ])

  const deliveryRadiusKm = vehicle?.deliveryRadiusKm ?? 10
  const locationsWithinRadius = useMemo(
    () =>
      areBookingLocationsWithinRadius({
        hostLocation: vehicle?.homeLocation,
        pickupLocation,
        dropoffLocation,
        deliveryRadiusKm,
      }),
    [deliveryRadiusKm, dropoffLocation, pickupLocation, vehicle?.homeLocation],
  )

  // Availability can also invalidate a range that was already stored (persisted
  // draft, host blocks the days while the screen is open). Erasing it silently
  // is the same defect as the untappable-then-vanishing return date, so say so.
  // The route remains mounted behind checkout. Creating a rental invalidates
  // availability; the server now omits this guest's own pending_payment hold
  // so a hidden calendar cannot grey out the dates they are paying for. A
  // hidden calendar must still never clear the draft over payment/3DS.
  useEffect(() => {
    if (!isFocused || !availabilityReady || !startDate || !selectedRangeHasUnavailable) return
    toast.warning(t('booking.dates_cleared_unavailable'))
    if (unavailableDateSet.has(startDate)) {
      setDates(null, null)
      setActiveDateField('pickup')
      return
    }
    setDates(startDate, null)
    setActiveDateField('return')
  }, [
    isFocused,
    availabilityReady,
    selectedRangeHasUnavailable,
    setDates,
    startDate,
    t,
    unavailableDateSet,
  ])

  useEffect(() => {
    if (!isFocused || !startDate || !endDate || !minimumReturnDate || endDate >= minimumReturnDate)
      return
    setDates(startDate, null)
    setActiveDateField('return')
  }, [isFocused, endDate, minimumReturnDate, setDates, startDate])

  // La hora que quedo fuera de la ventana se AJUSTA al borde valido, y el
  // store se entera. El picker ya pinta el valor derivado; esto es para que
  // el borrador y el checkout no sigan cargando la hora vieja. Un tick de
  // seleccion, como cuando el wheel de iOS se sienta en el minimo -- no un
  // toast que se evapora y deja el campo vacio.
  useEffect(() => {
    if (!isFocused || !availabilityReady) return
    const pickupChanged = Boolean(startTime && pickupTime !== startTime)
    const dropoffChanged = Boolean(endTime && dropoffTime !== endTime)
    if (!pickupChanged && !dropoffChanged) return
    if (pickupChanged) setStartTime(pickupTime)
    if (dropoffChanged) setEndTime(dropoffTime)
    selection()
  }, [
    isFocused,
    availabilityReady,
    dropoffTime,
    endTime,
    pickupTime,
    setEndTime,
    setStartTime,
    startTime,
  ])

  useEffect(() => {
    if (!isFocused || !availabilityReady || !firstBookable || !defaultReturnDate) return

    const startIsPast = Boolean(
      startDate && startTime && isBookingDateTimeInThePast(startDate, startTime),
    )
    const selectedDayHasNoSlot = Boolean(
      startDate &&
      earliestPickupTimeOnDate({
        date: startDate,
        changeoverMin: earliestPickupByDate.get(startDate),
      }) === null,
    )
    const hasNoRange = !startDate
    const hasRangeMissingTimes = Boolean(startDate && endDate && (!startTime || !endTime))
    if (
      !isE2eCheckoutPreset &&
      !hasNoRange &&
      !hasRangeMissingTimes &&
      !startIsPast &&
      !selectedDayHasNoSlot
    ) {
      return
    }

    if (
      !isE2eCheckoutPreset &&
      hasRangeMissingTimes &&
      !startIsPast &&
      !selectedDayHasNoSlot &&
      startDate &&
      endDate
    ) {
      const time = earliestPickupTimeOnDate({
        date: startDate,
        changeoverMin: earliestPickupByDate.get(startDate),
      })
      if (!time) return
      const nextEnd = clampTimeToWindow(time, undefined, latestReturnByDate.get(endDate)) ?? time
      if (startTime !== time) setStartTime(time)
      if (endTime !== nextEnd) setEndTime(nextEnd)
      return
    }

    const nextEndTime =
      clampTimeToWindow(firstBookable.time, undefined, latestReturnByDate.get(defaultReturnDate)) ??
      firstBookable.time

    if (
      startDate === firstBookable.date &&
      endDate === defaultReturnDate &&
      startTime === firstBookable.time &&
      endTime === nextEndTime
    ) {
      return
    }

    setDates(firstBookable.date, defaultReturnDate)
    setStartTime(firstBookable.time)
    setEndTime(nextEndTime)
    setActiveDateField('return')
  }, [
    isFocused,
    availabilityReady,
    defaultReturnDate,
    earliestPickupByDate,
    endDate,
    endTime,
    firstBookable,
    isE2eCheckoutPreset,
    latestReturnByDate,
    setDates,
    setEndTime,
    setStartTime,
    startDate,
    startTime,
  ])

  const calendarDayBlock = useCallback(
    (dateString: string): BookingCalendarBlock | null =>
      bookingCalendarDayBlock({
        tapped: dateString,
        minDate,
        availabilityReady,
        isHeld: unavailableDateSet.has(dateString),
        pickupSlot: earliestPickupTimeOnDate({
          date: dateString,
          changeoverMin: earliestPickupByDate.get(dateString),
        }),
        startDate,
        activeField: activeDateField,
        minReturnDate: minimumReturnDate,
        maxReturnDate: maxSelectableReturnDate,
      }),
    [
      activeDateField,
      availabilityReady,
      earliestPickupByDate,
      maxSelectableReturnDate,
      minDate,
      minimumReturnDate,
      startDate,
      unavailableDateSet,
    ],
  )

  const isCalendarDateDisabled = useCallback(
    (dateString: string) => calendarDayBlock(dateString) !== null,
    [calendarDayBlock],
  )

  const handleCalendarDayPress = useCallback(
    (date: string) => {
      const block = calendarDayBlock(date)
      if (block) {
        if (block === 'loading') return
        if (block === 'before_min' || block === 'no_pickup_slot') {
          toast.warning(
            t(block === 'before_min' ? 'booking.start_in_the_past' : 'booking.time_window_empty'),
          )
          return
        }
        if (block === 'held') {
          toast.warning(t('booking.errors.dates_blocked'))
          return
        }
        if (block === 'min_duration') {
          toast.warning(t('booking.minimum_duration_error', { count: minRentalDays ?? 0 }))
          return
        }
        toast.warning(t('booking.return_crosses_unavailable'))
        return
      }
      const next = applyDateRangeSelection({
        tapped: date,
        startDate,
        minReturnDate: minimumReturnDate,
        maxReturnDate: maxSelectableReturnDate,
        activeField: activeDateField,
      })
      if (!next) return
      setDates(next.startDate, next.endDate)
      setActiveDateField(next.field)
      productTelemetryService.recordEvent('booking_step', 'booking.date_selected', {
        elementId: 'booking.date-range-calendar',
        elementType: 'calendar',
        metadata: {
          field: next.endDate ? 'return' : 'pickup',
          minRentalHours: minRentalHours ?? 0,
        },
      })
    },
    [
      activeDateField,
      calendarDayBlock,
      maxSelectableReturnDate,
      minRentalDays,
      minRentalHours,
      minimumReturnDate,
      setDates,
      startDate,
      t,
    ],
  )

  const startIsPast = Boolean(
    startDate && pickupTime && isBookingDateTimeInThePast(startDate, pickupTime),
  )

  const canContinue =
    availabilityReady &&
    !selectedRangeHasUnavailable &&
    !!startDate &&
    !!endDate &&
    !!pickupTime &&
    !!dropoffTime &&
    !startIsPast &&
    days >= 1 &&
    !!breakdown &&
    !!vehicle &&
    locationsWithinRadius &&
    meetsVehicleMinimumDuration &&
    pairFeeReady

  const handleStartTimeChange = useCallback(
    (value: string) => {
      setStartTime(value)
      productTelemetryService.recordEvent('booking_step', 'booking.time_selected', {
        elementId: 'booking.pickup-time',
        elementType: 'time_picker',
        metadata: { field: 'pickup' },
      })
    },
    [setStartTime],
  )

  const handleEndTimeChange = useCallback(
    (value: string) => {
      setEndTime(value)
      productTelemetryService.recordEvent('booking_step', 'booking.time_selected', {
        elementId: 'booking.dropoff-time',
        elementType: 'time_picker',
        metadata: { field: 'return' },
      })
    },
    [setEndTime],
  )

  const handleDateFieldSelect = useCallback((field: ActiveDateField) => {
    setActiveDateField(field)
    productTelemetryService.recordEvent('booking_step', 'booking.date_field_selected', {
      elementId: field === 'pickup' ? 'booking.pickup-date' : 'booking.dropoff-date',
      elementType: 'button',
      metadata: { field },
    })
  }, [])

  const handleContinue = () => {
    if (!canContinue) return
    // Checkout lee el store, no la derivacion. Si el frame todavia tiene la
    // hora vieja, el huesped pagaria (o lo rechazarian) con un valor que esta
    // pantalla ya no muestra.
    if (startTime && pickupTime !== startTime) setStartTime(pickupTime)
    if (endTime && dropoffTime !== endTime) setEndTime(dropoffTime)
    productTelemetryService.recordEvent('booking_step', 'booking.checkout_continue', {
      elementId: 'booking.continue-button',
      elementType: 'button',
      metadata: {
        durationHours: selectedDurationHours,
        minRentalHours: minRentalHours ?? 0,
        priceBucket: bookingAmountBucket(breakdown?.totalCents),
        hasDeliveryOverride: Boolean(pickupAddress || dropoffAddress),
      },
    })
    router.push('/booking/checkout')
  }

  const handleSaveLocation = (location: BookingLocationValue) => {
    if (locationPickerMode === 'pickup') {
      setPickup(location.address, location.lat, location.lng)
      if (isSameLocation) setDropoff(location.address, location.lat, location.lng)
    } else if (locationPickerMode === 'dropoff') {
      setDropoff(location.address, location.lat, location.lng)
      setSameLocation(false)
    }
    setLocationPickerMode(null)
  }
  const handleBookingScroll = useProductTelemetryScroll('booking.selection.scroll')

  useEffect(() => {
    if (!isFocused || !isE2eCheckoutPreset || !canContinue) return
    if (startTime && pickupTime !== startTime) setStartTime(pickupTime)
    if (endTime && dropoffTime !== endTime) setEndTime(dropoffTime)
    router.replace('/booking/checkout')
  }, [
    isFocused,
    canContinue,
    dropoffTime,
    endTime,
    isE2eCheckoutPreset,
    pickupTime,
    router,
    setEndTime,
    setStartTime,
    startTime,
  ])

  // Shared scrollable form column. On desktop ScreenContainer caps it at
  // COLUMN_PRESETS.content (800px) and centers; on mobile it's full-width.
  // Bottom CTA is rendered inline at the end of the scroll so it lives
  // inside the same centered column — no more absolute-positioned bar
  // spanning the viewport under a narrow form.
  return (
    <ScreenContainer variant="content" scroll={false}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: insets.bottom + SPACING.lg }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        onScroll={handleBookingScroll}
        scrollEventThrottle={64}
      >
        <ScreenHeader
          title={t('booking.title')}
          backTestID="booking.back-button"
          style={{ paddingHorizontal: isDesktop ? 0 : SPACING.md }}
          titleStyle={isDesktop ? { fontSize: 28, lineHeight: 34 } : undefined}
        />

        {/* Vehicle header card */}
        {vehicle && (
          <View
            style={{
              marginHorizontal: isDesktop ? 0 : SPACING.md,
              marginBottom: SPACING.md,
              padding: SPACING.md,
              backgroundColor: colors.surface,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <Text
              style={{
                fontSize: 16,
                lineHeight: 22,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
              // The card holds a title and a price and has room to spare; one
              // line cut the trim off the thing being rented ("Nissan Frontier
              // 2025 — Automático, …"). Two lines fit the full title.
              numberOfLines={2}
            >
              {vehicle.title}
            </Text>
            <Text
              style={{
                fontSize: 14,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                marginTop: SPACING.xs,
              }}
            >
              {(() => {
                // Sticker price — identical to card / map pin / detail.
                const sticker = getVehicleDisplayPriceCents(vehicle)
                return formatPrice(sticker.cents, { unit: sticker.unit })
              })()}
            </Text>
          </View>
        )}

        {/* Un carro sin punto de entrega apaga Continuar PARA SIEMPRE:
            `areBookingLocationsWithinRadius` devuelve false cuando falta
            `hostLocation`, y `locationsWithinRadius` es una clausula de
            `canContinue`. El aviso ya estaba; lo que faltaba era la marca, y sin
            ella esta pantalla contaba como "apagado y mudo" aunque si lo
            explicaba. La otra marca de ubicacion --`radius`-- cuelga del bloque
            que exige `hasVehicleHomeLocation`, asi que aca es inalcanzable. */}
        {vehicle && !hasVehicleHomeLocation ? (
          <BookingInlineMessage
            title={t('checkout.vehicle_location_missing_title')}
            body={t('checkout.vehicle_location_missing')}
            colors={colors}
            isDesktop={isDesktop}
            testID="booking.why-disabled.vehicle-location"
          />
        ) : null}

        {/* The availability query has retry: false, and every date cell is
            disabled while `availabilityReady` is false. So one failed request
            left a calendar that looked normal and refused every tap, with
            nothing on screen explaining why and no way to ask again. */}
        {unavailableRangesError && (
          <View
            testID="booking.why-disabled.availability-error"
            style={{ marginHorizontal: isDesktop ? 0 : SPACING.md, marginBottom: SPACING.lg }}
          >
            <BookingInlineMessage
              title={t('booking.availability_error_title')}
              body={t('booking.availability_error_body')}
              colors={colors}
              isDesktop
            />
            <Button
              title={t('common.retry')}
              variant="secondary"
              size="md"
              onPress={() => void refetchAvailability()}
              testID="booking.availability-retry"
              accessibilityLabel={t('common.retry')}
            />
          </View>
        )}

        {/* Y el otro camino que quedaba mudo: la ficha del vehiculo. Sin ella no
            hay precio ni tarjeta del carro, y `canContinue` exige las dos, asi
            que el boton queda muerto sin explicacion y sin salida. Mismo aviso y
            mismo reintento que la disponibilidad. */}
        {/* Y mientras la ficha viaja, tampoco habia nada. Sin `vehicle` no se
            monta ni la tarjeta del carro, ni el precio, ni el aviso de duracion
            minima --todos cuelgan del vehiculo-- asi que el boton estaba apagado
            sobre una pantalla en blanco. Es el mismo hueco que la disponibilidad
            ya tenia tapado, en el otro dato que esta pantalla necesita. */}
        {vehicleLoading && !vehicle ? (
          <View
            testID="booking.why-disabled.vehicle-loading"
            accessible
            accessibilityLabel={t('common.loading')}
            style={{
              marginHorizontal: isDesktop ? 0 : SPACING.md,
              marginBottom: SPACING.lg,
              paddingVertical: SPACING.xl,
              alignItems: 'center',
              justifyContent: 'center',
              gap: SPACING.sm,
            }}
          >
            <ActivityIndicator size="small" color={colors.textSecondary} />
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                color: colors.textSecondary,
                textAlign: 'center',
              }}
            >
              {t('common.loading')}
            </Text>
          </View>
        ) : null}

        {vehicleError && !vehicle && (
          <View
            testID="booking.why-disabled.vehicle-error"
            style={{ marginHorizontal: isDesktop ? 0 : SPACING.md, marginBottom: SPACING.lg }}
          >
            <BookingInlineMessage
              title={t('common.error_title')}
              body={t('common.error_description')}
              colors={colors}
              isDesktop
            />
            <Button
              title={t('common.retry')}
              variant="secondary"
              size="md"
              onPress={() => void refetchVehicle()}
              testID="booking.vehicle-retry"
              accessibilityLabel={t('common.retry')}
            />
          </View>
        )}

        {vehicle && hasVehicleHomeLocation && pickupLocation && dropoffLocation && (
          <View
            style={{
              marginHorizontal: isDesktop ? 0 : SPACING.md,
              marginBottom: SPACING.lg,
              padding: SPACING.md,
              backgroundColor: colors.surface,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: colors.border,
              gap: SPACING.sm,
            }}
          >
            <Text style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
              {t('checkout.delivery_return_title')}
            </Text>
            <LocationSummaryRow
              label={t('checkout.pickup_at')}
              value={pickupLocation.address}
              colors={colors}
              onPress={() => setLocationPickerMode('pickup')}
            />
            <LocationSummaryRow
              label={t('checkout.dropoff_at')}
              value={dropoffLocation.address}
              colors={colors}
              onPress={() => setLocationPickerMode('dropoff')}
            />
            <RNPressable
              onPress={() => {
                const next = !isSameLocation
                setSameLocation(next)
                if (next) {
                  setDropoff(pickupLocation.address, pickupLocation.lat, pickupLocation.lng)
                }
              }}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: isSameLocation }}
              testID="booking.same-dropoff-location"
              style={{
                minHeight: INTERACTION.minTouchTarget,
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm,
              }}
            >
              <CheckCircle
                size={18}
                color={isSameLocation ? COLORS.coral.DEFAULT : colors.textMuted}
                strokeWidth={2}
              />
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  lineHeight: 18,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('checkout.same_return_location')}
              </Text>
            </RNPressable>
            <Text
              testID={locationsWithinRadius ? undefined : 'booking.why-disabled.radius'}
              style={{
                fontSize: 13,
                lineHeight: 18,
                fontFamily: FONTS.body,
                color: locationsWithinRadius ? colors.textSecondary : colors.error,
              }}
            >
              {locationsWithinRadius
                ? t('checkout.delivery_radius_notice', { radius: deliveryRadiusKm })
                : t('checkout.location_out_of_radius')}
            </Text>
          </View>
        )}

        {/* Date & time selection */}
        <View
          testID="date-selection.calendar"
          style={{
            paddingHorizontal: isDesktop ? 0 : SPACING.md,
            gap: SPACING.lg,
          }}
        >
          <Text style={{ fontSize: 18, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
            {t('booking.select_dates')}
          </Text>

          <View
            style={{
              flexDirection: 'row',
              gap: SPACING.sm,
            }}
          >
            {/* Empty pills say `ui.select_date` — the same string the DatePicker
                shows when it has no value, so "no date chosen yet" is worded
                one way in the whole product instead of one way per control.

                It is short on purpose. A pill's inner width is 128px on the
                narrowest phone we support; "Seleccioná tus fechas" (the section
                heading, which is what these used to echo) needed far more and
                rendered "Seleccioná tus f…" in both chips, truncating the
                heading 40pt above them. "Elegí fecha" needs 85px.

                The labels are short for the same measured reason: "Fecha de
                devolución" is exactly 128px at 12px, so it ellipsised to
                "Fecha de devoluci…". The heading already says these are dates,
                which is what makes "Recogida" / "Devolución" enough. */}
            <View style={{ flex: 1 }} testID="date-selection.start-date">
              <DateFieldButton
                label={t('booking.pickup_date')}
                value={startDate ? formatSvDateDisplay(startDate) : null}
                active={activeDateField === 'pickup'}
                onPress={() => handleDateFieldSelect('pickup')}
                colors={colors}
                testID="booking.pickup-date"
              />
            </View>
            <View style={{ flex: 1 }} testID="date-selection.end-date">
              <DateFieldButton
                label={t('booking.dropoff_date')}
                value={endDate ? formatSvDateDisplay(endDate) : null}
                active={activeDateField === 'return'}
                onPress={() => handleDateFieldSelect(startDate ? 'return' : 'pickup')}
                colors={colors}
                testID="booking.dropoff-date"
              />
            </View>
          </View>

          {minRentalHours ? (
            <DurationMessage
              tone={startDate && endDate && !meetsVehicleMinimumDuration ? 'error' : 'info'}
              text={
                startDate && endDate && !meetsVehicleMinimumDuration
                  ? t('booking.minimum_duration_error', { count: minRentalDays ?? 0 })
                  : t('booking.minimum_duration_notice', { count: minRentalDays ?? 0 })
              }
              colors={colors}
              testID={
                startDate && endDate && !meetsVehicleMinimumDuration
                  ? 'booking.why-disabled.min-duration'
                  : undefined
              }
            />
          ) : null}

          {hasNoSelectableReturnDate ? (
            <BookingInlineMessage
              title={t('booking.return_unavailable_title')}
              body={t('booking.return_unavailable_body')}
              colors={colors}
              isDesktop
              style={{ marginBottom: 0 }}
            />
          ) : null}

          <DateRangeCalendar
            startDate={startDate}
            endDate={endDate}
            minDate={minDate}
            maxDate={availabilityToDate}
            activeField={activeDateField}
            onSelectDate={handleCalendarDayPress}
            isDateDisabled={isCalendarDateDisabled}
            firstAvailableStartDate={firstAvailableStartDate}
            firstAvailableReturnDate={firstAvailableReturnDate}
            tomorrow={tomorrow}
            loading={unavailableRangesLoading}
            loadingLabel={t('booking.availability_loading')}
            testID="booking.date-range-calendar"
          />

          {startDate || endDate ? (
            <View
              style={{
                flexDirection: timeFieldsInline ? 'row' : 'column',
                gap: SPACING.lg,
                alignItems: timeFieldsInline ? 'flex-start' : undefined,
              }}
            >
              {startDate ? (
                <View style={timeFieldsInline ? { flex: 1, minWidth: 0 } : undefined}>
                  <TimeSlotPicker
                    value={pickupTime}
                    onChange={handleStartTimeChange}
                    label={t('booking.pickup_time')}
                    placeholder={t('booking.select_time')}
                    testID="booking.pickup-time"
                    minTime={pickupWindowStart}
                    hint={
                      pickupWindowStart
                        ? t('booking.pickup_window_hint', {
                            time: formatTime12h(pickupWindowStart),
                          })
                        : undefined
                    }
                    error={pickupWindowImpossible ? t('booking.time_window_empty') : undefined}
                  />
                </View>
              ) : null}

              {endDate ? (
                <View style={timeFieldsInline ? { flex: 1, minWidth: 0 } : undefined}>
                  <TimeSlotPicker
                    value={dropoffTime}
                    onChange={handleEndTimeChange}
                    label={t('booking.dropoff_time')}
                    placeholder={t('booking.select_time')}
                    testID="booking.dropoff-time"
                    maxTime={dropoffWindowEnd}
                    hint={
                      dropoffWindowEnd
                        ? t('booking.dropoff_window_hint', {
                            time: formatTime12h(dropoffWindowEnd),
                          })
                        : undefined
                    }
                    error={dropoffWindowImpossible ? t('booking.time_window_empty') : undefined}
                  />
                </View>
              ) : null}
            </View>
          ) : null}

          {/* UN BOTON APAGADO QUE NO DICE POR QUE ES UNA TRAMPA, y esta en la
              ruta del dinero.
              Con las fechas puestas y sin horas, `canContinue` era falso y el
              boton quedaba muerto sin una sola palabra: se elegia el rango, se
              tocaba Continuar y no pasaba nada. Los otros motivos por los que
              este boton se apaga YA se explican en pantalla --el radio de
              entrega arriba, y la duracion minima en su propio mensaje-- asi
              que el unico que moria callado era este.
              Encontrado caminando el recorrido exacto del revisor de Apple
              contra produccion: llegaba hasta las fechas y ahi se atoraba.

              El aviso vive JUNTO A LOS SELECTORES DE HORA, no pegado al boton:
              lo que hay que hacer es llenar esos dos campos, y ahi es donde
              esta la mano y la mirada. Ademas comparte el `gap` de esta columna
              con el mensaje de duracion minima, en vez de quedar aplastado
              contra el boton dentro de la FormActionBar. */}
          {startDate && endDate && startIsPast ? (
            <DurationMessage
              tone="info"
              text={t('booking.start_in_the_past')}
              colors={colors}
              testID="booking.why-disabled.past-start"
            />
          ) : null}

          {startDate && endDate && (!pickupTime || !dropoffTime) && !startIsPast ? (
            <DurationMessage
              tone="info"
              text={t('booking.pick_times_to_continue')}
              colors={colors}
              testID="booking.why-disabled.times"
            />
          ) : null}

          {days > 0 && (
            <Text
              style={{
                fontSize: 14,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                textAlign: 'center',
              }}
            >
              {t('booking.days', { count: days })}
            </Text>
          )}
        </View>

        {/* Inline CTA — lives inside the centered column, no more
            absolute bar spanning viewport beneath a narrow form. */}
        <FormActionBar testID="date-selection.confirm-button" style={{ marginTop: SPACING.xl }}>
          {/* The 15% service fee and IVA used to appear for the first time on
              the checkout screen, after the guest had already committed to the
              dates. `breakdown` is already computed here — show what they will
              actually pay next to the button that takes them there. */}
          {breakdown && (
            <View style={{ gap: SPACING.sm, marginBottom: SPACING.md }}>
              <RepeatPairFeeCard
                completedPairTrips={completedPairTrips}
                feePct={guestServiceFeePct}
                compact
              />
              <CoverageStayReasonCard compact />
            </View>
          )}
          {breakdown && (
            <>
              {isIndependenceCampaignApplied ? (
                <View
                  testID="booking.independence-campaign"
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: SPACING.sm,
                    marginBottom: SPACING.sm,
                  }}
                >
                  <CheckCircle size={16} color={colors.success} strokeWidth={2} />
                  <Text
                    style={{
                      flex: 1,
                      fontSize: 13,
                      lineHeight: 18,
                      fontFamily: FONTS.body,
                      color: colors.success,
                    }}
                  >
                    {t('booking.independence_promo_applied')}
                  </Text>
                </View>
              ) : null}
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: SPACING.md,
                  marginBottom: SPACING.md,
                }}
              >
                <Text style={{ fontSize: 16, fontFamily: FONTS.body, color: colors.textPrimary }}>
                  {t('booking.charged_today')}
                </Text>
                <Text
                  testID="booking.total-preview"
                  style={{ fontSize: 20, fontFamily: FONTS.bodyBold, color: colors.textAccent }}
                >
                  {formatCurrency(breakdown.totalCents)}
                </Text>
              </View>
            </>
          )}
          <Button
            title={t('common.continue')}
            variant="primary"
            size="lg"
            onPress={handleContinue}
            disabled={!canContinue}
            testID="booking.continue-button"
            accessibilityRole="button"
            accessibilityLabel={t('common.continue')}
          />
        </FormActionBar>

        {hasVehicleHomeLocation && vehicle?.homeLocation && pickupLocation && dropoffLocation && (
          <BookingLocationPicker
            visible={locationPickerMode !== null}
            mode={locationPickerMode ?? 'pickup'}
            initialLocation={locationPickerMode === 'dropoff' ? dropoffLocation : pickupLocation}
            hostLocation={vehicle.homeLocation}
            deliveryRadiusKm={deliveryRadiusKm}
            onClose={() => setLocationPickerMode(null)}
            onSave={handleSaveLocation}
          />
        )}
      </ScrollView>
    </ScreenContainer>
  )
}

function LocationSummaryRow({
  label,
  value,
  colors,
  onPress,
}: {
  label: string
  value: string
  colors: ReturnType<typeof useSemanticColors>
  onPress: () => void
}) {
  return (
    <RNPressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${value}`}
      style={{
        minHeight: 48,
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        borderRadius: RADIUS.md,
      }}
    >
      <MapPin size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 12,
            lineHeight: 16,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
          numberOfLines={1}
        >
          {label}
        </Text>
        <Text
          style={{
            marginTop: 2,
            fontSize: 14,
            lineHeight: 19,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
          numberOfLines={2}
        >
          {value}
        </Text>
      </View>
      <Pencil size={17} color={colors.textMuted} strokeWidth={2} />
    </RNPressable>
  )
}

function BookingInlineMessage({
  title,
  body,
  colors,
  isDesktop,
  style,
  testID,
}: {
  title: string
  body: string
  colors: ReturnType<typeof useSemanticColors>
  isDesktop: boolean
  /** Callers inside a `gap`-spaced column pass `marginBottom: 0`. */
  style?: StyleProp<ViewStyle>
  testID?: string
}) {
  return (
    <View
      testID={testID}
      style={[
        {
          marginHorizontal: isDesktop ? 0 : SPACING.md,
          marginBottom: SPACING.lg,
          padding: SPACING.md,
          borderRadius: RADIUS.lg,
          borderWidth: 1,
          borderColor: colors.warning,
          backgroundColor: `${colors.warning}12`,
          flexDirection: 'row',
          gap: SPACING.sm,
          alignItems: 'flex-start',
        },
        style,
      ]}
    >
      <AlertCircle size={19} color={colors.warning} strokeWidth={2} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: 14,
            lineHeight: 19,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {title}
        </Text>
        <Text
          style={{
            marginTop: 2,
            fontSize: 13,
            lineHeight: 18,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {body}
        </Text>
      </View>
    </View>
  )
}

function DurationMessage({
  tone,
  text,
  colors,
  testID,
}: {
  tone: 'info' | 'error'
  text: string
  colors: ReturnType<typeof useSemanticColors>
  testID?: string
}) {
  const accent = tone === 'error' ? colors.error : COLORS.coral.DEFAULT

  return (
    <View
      testID={testID}
      style={{
        minHeight: 42,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: `${accent}44`,
        backgroundColor: `${accent}10`,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.sm,
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
      }}
    >
      <Clock3 size={17} color={accent} strokeWidth={2} />
      <Text
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          lineHeight: 18,
          fontFamily: FONTS.body,
          color: tone === 'error' ? colors.error : colors.textSecondary,
        }}
      >
        {text}
      </Text>
    </View>
  )
}

function DateFieldButton({
  label,
  value,
  active,
  onPress,
  colors,
  testID,
}: {
  label: string
  /** The formatted date, or null when nothing has been chosen yet. */
  value: string | null
  active: boolean
  onPress: () => void
  colors: ReturnType<typeof useSemanticColors>
  testID: string
}) {
  const { t } = useTranslation()
  return (
    <RNPressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      testID={testID}
      style={{
        flex: 1,
        minHeight: 58,
        borderRadius: RADIUS.md,
        borderWidth: 1,
        borderColor: active ? COLORS.coral.DEFAULT : colors.border,
        backgroundColor: active ? `${COLORS.coral.DEFAULT}10` : colors.surface,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.sm,
        justifyContent: 'center',
      }}
    >
      <Text
        style={{
          fontSize: 12,
          lineHeight: 16,
          fontFamily: FONTS.body,
          color: active ? colors.textAccent : colors.textSecondary,
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
      {/* An empty field has to look empty.
          Both lines used to render bodyBold in textPrimary whether or not a
          date had been picked, so "Seleccionar fecha" arrived with the exact
          weight and colour of "04/08/2026" — the one state where the user most
          needs to see that something is missing is the state that looked
          filled in. Placeholders are muted and regular everywhere else in the
          product; this is the same ramp TextInput and DatePicker use.

          The text no longer shrinks to fit either. Measured at 15px
          Montserrat on the narrowest phone we support, a pill's inner width is
          128px: "04/08/2026" needs 90 and "Seleccionar fecha" needed 136. So
          the moment you picked ONE of the two dates, the filled pill stayed at
          15px and the empty one dropped to 12.75 — two controls side by side,
          same row, different type sizes. "Elegí fecha" needs 85px and fits at
          every width, which is what lets the shrinking go away instead of
          being tuned. Text that resizes itself is also exactly what the
          large-font work forbids: the layout yields, the type does not. */}
      <Text
        style={{
          marginTop: 2,
          fontSize: 15,
          lineHeight: 20,
          fontFamily: value ? FONTS.bodyBold : FONTS.body,
          color: value ? colors.textPrimary : colors.textMuted,
        }}
        numberOfLines={1}
      >
        {value ?? t('ui.select_date')}
      </Text>
    </RNPressable>
  )
}
